import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { MIN_SEARCH_LENGTH } from '@/constants';

export interface CustomAutocompleteProps {
  value?: string;
  suggestions?: string[];
  onSearch: (query: string) => void | Promise<void>;
  onChange?: (value: string) => void;
  onSelect?: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  panelClassName?: string;
  itemTemplate?: (item: string) => React.ReactNode;
  minLength?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  showDropdownButton?: boolean;
}

export const CustomAutocomplete = forwardRef<
  HTMLInputElement,
  CustomAutocompleteProps
>(
  (
    {
      value = '',
      suggestions = [],
      onSearch,
      onChange,
      onSelect,
      placeholder,
      className = '',
      inputClassName = '',
      panelClassName = '',
      itemTemplate,
      minLength = MIN_SEARCH_LENGTH,
      autoFocus = false,
      disabled = false,
      showDropdownButton = true,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    useImperativeHandle(ref, () => inputRef.current!);

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    useEffect(() => {
      if (autoFocus && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [autoFocus]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
      if (highlightedIndex >= 0 && listRef.current) {
        const highlightedElement = listRef.current.children[
          highlightedIndex
        ] as HTMLElement;
        if (
          highlightedElement &&
          typeof highlightedElement.scrollIntoView === 'function'
        ) {
          highlightedElement.scrollIntoView({ block: 'nearest' });
        }
      }
    }, [highlightedIndex]);

    const handleInputChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange?.(newValue);

        if (newValue.length >= minLength) {
          await onSearch(newValue);

          setIsOpen(true);
          setHighlightedIndex(-1);
        } else {
          setIsOpen(false);
        }
      },
      [onChange, onSearch, minLength],
    );

    const handleInputClick = useCallback(async () => {
      if (disabled) return;

      if (inputValue.length >= minLength && suggestions.length > 0) {
        setIsOpen(true);
      } else if (inputValue.length >= minLength) {
        await onSearch(inputValue);
        setIsOpen(true);
      }
    }, [disabled, inputValue, minLength, suggestions.length, onSearch]);

    const handleItemClick = useCallback(
      (item: string) => {
        setInputValue(item);
        onChange?.(item);
        onSelect?.(item);
        setIsOpen(false);
        setHighlightedIndex(-1);
      },
      [onChange, onSelect],
    );

    const handleDropdownClick = useCallback(async () => {
      if (disabled) return;

      if (!isOpen) {
        await onSearch(inputValue || '');
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }, [disabled, isOpen, onSearch, inputValue]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen && e.key !== 'ArrowDown' && e.key !== 'Enter') return;

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            if (!isOpen) {
              handleDropdownClick();
            } else {
              setHighlightedIndex((prev) =>
                prev < suggestions.length - 1 ? prev + 1 : prev,
              );
            }
            break;
          case 'ArrowUp':
            e.preventDefault();
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
            break;
          case 'Enter':
            e.preventDefault();
            if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
              handleItemClick(suggestions[highlightedIndex]);
            }
            break;
          case 'Escape':
            e.preventDefault();
            setIsOpen(false);
            setHighlightedIndex(-1);
            break;
        }
      },
      [
        isOpen,
        suggestions,
        highlightedIndex,
        handleItemClick,
        handleDropdownClick,
      ],
    );

    const renderItem = (item: string) => {
      return itemTemplate ? itemTemplate(item) : item;
    };

    console.log(
      '[CustomAutocomplete] render - isOpen:',
      isOpen,
      'suggestions.length:',
      suggestions.length,
      'suggestions:',
      suggestions,
    );

    return (
      <div
        ref={containerRef}
        className={`relative inline-block w-full ${className}`}
      >
        <div className="relative flex items-center">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={`flex-1 rounded border border-gray-300 py-2 pr-8 pl-2 text-sm transition-all outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-60 ${inputClassName}`}
          />
          {showDropdownButton && (
            <button
              type="button"
              className="absolute right-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded border-0 bg-transparent p-0 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={handleDropdownClick}
              disabled={disabled}
              tabIndex={-1}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M7 10L3 6H11L7 10Z" fill="currentColor" />
              </svg>
            </button>
          )}
        </div>

        {isOpen && suggestions.length > 0 && (
          <div
            className={`absolute top-full left-0 z-[2000] mt-1 w-full rounded border border-gray-300 bg-white shadow-lg ${panelClassName}`}
          >
            <ul
              ref={listRef}
              className="m-0 max-h-60 list-none overflow-x-hidden overflow-y-auto p-0"
            >
              {suggestions.map((item, index) => (
                <li
                  key={index}
                  className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                    index === highlightedIndex
                      ? 'bg-blue-100'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {renderItem(item)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  },
);

CustomAutocomplete.displayName = 'CustomAutocomplete';
